import * as React from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Button, Typography } from '@mui/material';
import axiosInstant from '../../api';
import { IBrandDetailsContext } from './SingleBrandDetails';
import { WhiteBackground } from '../../components/WhiteBackground';
import { PageTitle } from '../../components/PageTitle';
import { IconPlus } from '../../utils/icons';
import IconSearch from '../../assets/svg/search-icon-grey.svg';
import { DotLottleLoader } from '../../components/Loader/DotLottleLoader';
import { BrandDetailExpertsList } from './BrandDetailExpertsList';
import UpdateExpertListModal from './UpdateExpertListModal';
import { useUser } from '../../hooks/useUser';
import { useCommonParams } from '../../hooks/useCommonParams';

export function BrandDetailExpertsPanel() {
  const { id } = useParams();
  const { role } = useUser();

  const {
    brandExperts: prevPros,
    getBrandExperts: refetchPros,
  } = useOutletContext() as IBrandDetailsContext;

  const { searchText: searchPros, setSearchText: setSearchPros } = useCommonParams();
  const [loading, setLoading] = React.useState(false);
  const [showAddExpertModal, setShowAddExpertModal] = React.useState(false);

  const isSalesTeam = role === 'SALES_TEAM';

  const fetchExperts = async () => {
    setLoading(true);
    await refetchPros();
    setLoading(false);
  };

  const handleSaveChanges = async (prosIds: number[]) => {
    try {
      setLoading(true);
      if (id) {
        await axiosInstant.post(`/brands/${id}/pros`, {
          user_ids: prosIds,
        });

        toast.success('Brand updated successfully!');
      }
    } catch (error) {
      toast.error('Failed to update brand. Please try again.');
    } finally {
      refetchPros();
      setLoading(false);
    }
  };

  if (!id) return null;

  const filteredPros = prevPros.filter((pro) =>
    pro.displayName?.toLowerCase().includes(searchPros.toLowerCase())
  );

  return (
    <WhiteBackground>
      <div className="flex flex-col gap-6">
        <div className="flex items-center justify-between">
          <PageTitle title="Experts" />
          <div
            style={{
              cursor: isSalesTeam ? 'not-allowed' : 'default',
            }}
          >
            <Button
              variant="contained"
              color="primary"
              className="gap-[2px] !py-3 px-4"
              onClick={() => setShowAddExpertModal(true)}
              disabled={isSalesTeam}
              sx={{
                '&.Mui-disabled': {
                  backgroundColor: 'var(--accent)',
                  color: '#FFF',
                  opacity: 0.5,
                },
              }}
            >
              <IconPlus />
              <Typography variant="body1" className="capitalize !font-bold">
                Add Expert
              </Typography>
            </Button>
          </div>
        </div>

        <div className="flex justify-center">
          <div className="w-full relative">
            <img
              className="absolute top-1/2 transform -translate-x-1/2 -translate-y-1/2 left-5"
              src={IconSearch}
              alt="search-icon"
            />
            <input
              type="text"
              className="w-[333px] h-[40px] px-[2.25rem] py-0 gap-2 bg-surface-2 text-text-2 placeholder-muted rounded-xl focus:outline-none focus:text-gray-700"
              id="search-experts"
              placeholder="Search"
              value={searchPros}
              onChange={(e) => setSearchPros(e.target.value)}
            />
          </div>
        </div>

        {loading ? (
          <DotLottleLoader />
        ) : (
          <BrandDetailExpertsList
            experts={filteredPros}
            refetch={fetchExperts}
          />
        )}
      </div>
      {showAddExpertModal && (
        <UpdateExpertListModal
          experts={prevPros}
          onClose={() => setShowAddExpertModal(false)}
          onSave={handleSaveChanges}
        />
      )}
    </WhiteBackground>
  );
}
